'use strict'

let dom = {};

function getI18n() {
  const i18n = window.searchPaneI18n || {};

  return {
    searching: i18n.searching || 'Searching...',
    searchError: i18n.searchError || 'Search failed.',
    searchResultsUpdated: i18n.searchResultsUpdated || 'Search results updated.'
  };
}

function toArray(nodeList) {
  return Array.prototype.slice.call(nodeList || []);
}

function setAriaHidden(element, hidden) {
  if (!element) {
    return;
  }

  element.setAttribute('aria-hidden', hidden ? 'true' : 'false');
}

function setExpanded(elements, expanded) {
  elements.forEach(function (element) {
    element.setAttribute('aria-expanded', expanded ? 'true' : 'false');
  });
}

function isSearchPaneCloseButton(element) {
  return !!(element && containsOrIs(dom.searchPane, element));
}

function containsOrIs(container, target) {
  return !!(container && target && (container === target || container.contains(target)));
}

function clearResults() {
  if (!dom.results) {
    return;
  }

  dom.results.innerHTML = '';
  dom.results.classList.add('d-none');
  setAriaHidden(dom.results, true);
}

function announce(message) {
  if (!dom.status) {
    return;
  }

  dom.status.textContent = '';
  window.setTimeout(function () {
    dom.status.textContent = message || '';
  }, 100);
}

function extractAnnouncementFromResponse(responseText, fallbackMessage) {
  const wrapper = document.createElement('div');
  let heading = null;
  let noResults = null;

  wrapper.innerHTML = responseText;
  heading = wrapper.querySelector('h1, h2, h3');
  noResults = wrapper.querySelector('p');

  if (heading && heading.textContent.trim()) {
    return heading.textContent.trim();
  }

  if (noResults && noResults.textContent.trim()) {
    return noResults.textContent.trim();
  }

  return fallbackMessage;
}

function focusFirstResult() {
  const firstResult = dom.results ? dom.results.querySelector('.search-pane__result-link') : null;

  if (firstResult && typeof firstResult.focus === 'function') {
    window.setTimeout(function () {
      firstResult.focus();
    }, 0);
  }
}

const searchPane = {
  searchTimer: null,
  searchXhr: null,
  lastSearchText: null,
  searchPaneVisible: false,
  lastTrigger: null,

  toggleSearchPane(trigger) {
    this.searchPaneVisible = !this.searchPaneVisible;

    if (this.searchPaneVisible) {
      this.lastTrigger = trigger || document.activeElement || null;
      setAriaHidden(dom.searchPane, false);
      setExpanded(dom.expandableTogglers, true);

      window.setTimeout(function () {
        if (dom.searchField) {
          dom.searchField.focus();
        }
      }, 100);

      return;
    }

    setAriaHidden(dom.searchPane, true);
    setExpanded(dom.expandableTogglers, false);

    if (this.lastTrigger && typeof this.lastTrigger.focus === 'function') {
      this.lastTrigger.focus();
    } else if (dom.togglers.length) {
      dom.togglers[0].focus();
    }
  },

  isWithinSearchPane(target) {
    if (containsOrIs(dom.searchPane, target)) {
      return true;
    }

    return dom.togglers.some(function (toggler) {
      return containsOrIs(toggler, target);
    });
  },

  abortActiveRequest() {
    if (this.searchXhr) {
      this.searchXhr.abort();
      this.searchXhr = null;
    }
  },

  doSearch() {
    const self = this;
    const i18n = getI18n();
    const searchText = dom.searchField.value.trim();

    window.clearTimeout(self.searchTimer);

    if (!searchText) {
      self.lastSearchText = null;
      self.abortActiveRequest();
      clearResults();
      announce('');
      if (dom.loading) {
        dom.loading.classList.add('d-none');
        setAriaHidden(dom.loading, true);
      }
      return;
    }

    if (searchText === self.lastSearchText) {
      return;
    }

    self.abortActiveRequest();
    clearResults();

    if (dom.loading) {
      dom.loading.classList.remove('d-none');
      setAriaHidden(dom.loading, false);
    }
    announce(i18n.searching);

    self.searchTimer = window.setTimeout(function () {
      const request = new XMLHttpRequest();
      const year = dom.body ? dom.body.getAttribute('data-year') : '';
      const language = document.documentElement.getAttribute('lang') || '';
      const query = [
        'stage-language=' + encodeURIComponent(language),
        'search=' + encodeURIComponent(searchText)
      ].join('&');

      self.searchXhr = request;

      request.open('GET', '/stage-ajax/' + year + '-search-pane/?' + query, true);
      request.setRequestHeader('X-Requested-With', 'XMLHttpRequest');

      request.onreadystatechange = function () {
        if (request.readyState !== 4 || self.searchXhr !== request) {
          return;
        }

        self.searchXhr = null;

        if (dom.loading) {
          dom.loading.classList.add('d-none');
          setAriaHidden(dom.loading, true);
        }

        if (request.status >= 200 && request.status < 300) {
          self.lastSearchText = searchText;
          dom.results.innerHTML = request.responseText;
          announce(extractAnnouncementFromResponse(request.responseText, i18n.searchResultsUpdated));
        } else {
          dom.results.textContent = i18n.searchError;
          announce(i18n.searchError);
        }

        dom.results.classList.remove('d-none');
        setAriaHidden(dom.results, false);

        if (request.status >= 200 && request.status < 300) {
          focusFirstResult();
        }
      };

      request.onerror = function () {
        if (self.searchXhr !== request) {
          return;
        }

        self.searchXhr = null;

        if (dom.loading) {
          dom.loading.classList.add('d-none');
          setAriaHidden(dom.loading, true);
        }

        dom.results.textContent = i18n.searchError;
        dom.results.classList.remove('d-none');
        setAriaHidden(dom.results, false);
        announce(i18n.searchError);
      };

      request.onabort = function () {
        if (self.searchXhr === request) {
          self.searchXhr = null;
        }
      };

      request.send();
    }, 500);
  },

  init() {
    const self = this;

    dom.body = document.body;
    dom.togglers = toArray(document.querySelectorAll('[data-toggle="search-pane"]'));
    dom.expandableTogglers = [];
    dom.searchPane = document.querySelector('.search-pane');

    if (!dom.searchPane || !dom.togglers.length) {
      return;
    }

    dom.searchField = dom.searchPane.querySelector('.search-pane__search-field');
    dom.searchButton = dom.searchPane.querySelector('.search-pane__search-button');
    dom.loading = dom.searchPane.querySelector('.search-pane__loading');
    dom.results = dom.searchPane.querySelector('.search-pane__results');
    dom.status = dom.searchPane.querySelector('.search-pane__status');
    dom.expandableTogglers = dom.togglers.filter(function (toggler) {
      return !isSearchPaneCloseButton(toggler);
    });

    if (!dom.searchField || !dom.searchButton || !dom.results) {
      return;
    }

    dom.togglers.forEach(function (toggler) {
      toggler.addEventListener('click', function (evt) {
        evt.stopPropagation();
        self.toggleSearchPane(toggler);
      });
    });

    document.body.addEventListener('click', function (evt) {
      if (self.searchPaneVisible && !dom.searchPane.contains(evt.target)) {
        self.toggleSearchPane();
      }
    });

    document.addEventListener('focusin', function (evt) {
      if (self.searchPaneVisible && !self.isWithinSearchPane(evt.target)) {
        self.toggleSearchPane();
      }
    });

    document.addEventListener('keydown', function (evt) {
      if (self.searchPaneVisible && (evt.key === 'Escape' || evt.keyCode === 27)) {
        evt.stopPropagation();
        self.toggleSearchPane();
      }
    });

    dom.searchField.addEventListener('keydown', function (evt) {
      if (evt.key === 'Enter' || evt.keyCode === 13) {
        evt.preventDefault();
        evt.stopPropagation();
        self.doSearch();
      }
    });

    dom.searchButton.addEventListener('click', function (evt) {
      evt.preventDefault();
      self.doSearch();
    });

    setExpanded(dom.expandableTogglers, false);
  }
}

module.exports = searchPane;
